<template>
  <div>
    <reports-title :title="pageTitle" />
    <reports-toolbar
      :drop-menu="menu"
      :report-title="pageTitle"
      :range-value="toolbarRange"
      :toolbar-disabled="isStatic || isDisabled"
      @onSelectDropdown="onChangeCalculationMode"
    />
    <skeleton-wrapper
      v-if="isShowSkeleton"
      id="skeletonWrapper"
    >
      <template #body>
        <line-and-bar-chart
          :stub-config="stubConfig"
          :is-static="true"
          :custom-legends="true"
          :show-stub-message="false"
        />
      </template>
    </skeleton-wrapper>
    <line-and-bar-chart
      v-else
      id="report_export_id"
      ref="chart"
      :config="chartConfig"
      :stub-config="stubConfig"
      :is-static="isStatic"
      :custom-legends="true"
      @toggleSeries="toggleSeries"
      @onAllLegendsDisabled="flag => isDisabled = flag"
    />
  </div>
</template>

<script>
import app from '$$app';
import moment from 'moment';
import constants from '$$helpers/constants';
import timeReportConfigs from '../time/chartsConfig';
import budgetReportConfigs from '../budget/chartsConfig';
import MonthTimeReport from '../time/monthTimeReport';
import MonthBudgetReport from '../budget/monthBudgetReport';
import reportConstants from './constants';
import reportsTitle from './title.vue';
import reportsToolbar from './toolbar.vue';
import lineAndBarChart from './lineAndBarChart.vue';
import reportHelpers from './helpers';
import skeletonWrapper from '../../common/VueComponents/skeletons/skeletonWrapper.vue';
import statusHelper from '../../../helpers/status';

const MONTH_LOCALES = [
  __('report_month_jan'),
  __('report_month_feb'),
  __('report_month_march'),
  __('report_month_apr'),
  __('report_month_may'),
  __('report_month_jun'),
  __('report_month_jul'),
  __('report_month_aug'),
  __('report_month_sep'),
  __('report_month_oct'),
  __('report_month_nov'),
  __('report_month_dec'),
];

export default {
  components: {
    reportsTitle, reportsToolbar, lineAndBarChart, skeletonWrapper,
  },
  props: {
    reportType: { type: String, required: true, default: 'budget' },
  },
  data() {
    return {
      locale: __,
      appListeners: [],
      projectId: null,
      baselines: [],
      baselineData: {},
      baselineId: null,
      chartConfig: {},
      clientWidth: 0,
      calculationMode: constants.OVERVIEW_TYPES.ACTUAL_TO_PLANNED,
      toolbarRange: '',
      isStatic: true,
      isShowSkeleton: true,
      isDisabled: false,
    };
  },
  computed: {
    pageTitle() {
      if (this.$route?.name === 'timeReports') {
        return reportConstants.TITLE.time.timeByMonth;
      }
      if (this.$route?.name === 'budgetReports') {
        return reportConstants.TITLE.budget.budgetByMonth;
      }

      return '';
    },
    menu() {
      return [
        {
          type: 'item',
          name: constants.OVERVIEW_TYPES.ACTUAL_TO_PLANNED,
          text: __(`overview_type_${constants.OVERVIEW_TYPES.ACTUAL_TO_PLANNED}`),
          isActive: this.calculationMode === constants.OVERVIEW_TYPES.ACTUAL_TO_PLANNED,
          isDisabled: this.isStatic,
          tooltip: { content: __(`reports_pages_calculation_model_tooltip_${constants.OVERVIEW_TYPES.ACTUAL_TO_PLANNED}_single_report`), position: 'right' },
        },
        {
          type: 'submenu',
          name: constants.OVERVIEW_TYPES.ACTUAL_TO_BASELINE,
          text: __(`overview_type_${constants.OVERVIEW_TYPES.ACTUAL_TO_BASELINE}`),
          list: this.baselines,
          isActive: this.calculationMode === constants.OVERVIEW_TYPES.ACTUAL_TO_BASELINE,
          isDisabled: !this.baselines.length || this.isStatic,
          selectedSubmenuId: this.calculationMode === constants.OVERVIEW_TYPES.ACTUAL_TO_BASELINE ? this.baselineId : null,
          tooltip: !this.baselines.length ? { content: __(`reports_pages_calculation_model_tooltip_${constants.OVERVIEW_TYPES.ACTUAL_TO_BASELINE}_single_report`), position: 'right' } : null,
        },
        {
          type: 'submenu',
          name: constants.OVERVIEW_TYPES.PLANNED_TO_BASELINE,
          text: __(`overview_type_${constants.OVERVIEW_TYPES.PLANNED_TO_BASELINE}`),
          list: this.baselines,
          isActive: this.calculationMode === constants.OVERVIEW_TYPES.PLANNED_TO_BASELINE,
          isDisabled: !this.baselines.length || this.isStatic,
          selectedSubmenuId: this.calculationMode === constants.OVERVIEW_TYPES.PLANNED_TO_BASELINE ? this.baselineId : null,
          tooltip: !this.baselines.length ? { content: __(`reports_pages_calculation_model_tooltip_${constants.OVERVIEW_TYPES.PLANNED_TO_BASELINE}_single_report`), position: 'right' } : null,
        },
      ];
    },
    leftSidebarWidth() {
      return this.$store.getters.leftSidebarWidth;
    },
    currentFilter() {
      return this.$store.getters['filter/currentFilter'];
    },
    allTasks() {
      return this.$store.getters['tasksModel/getTasksForGantt'](this.projectId).data;
    },
    baselineTasks() {
      if (this.baselineData?.tasks) {
        const baselineTasks = this.baselineData.tasks;

        return baselineTasks;
      }

      return [];
    },
    reportChartConfigs() {
      let configs = {};

      if (this.reportType === 'budget') {
        configs = budgetReportConfigs.monthReport;
      }

      if (this.reportType === 'time') {
        configs = timeReportConfigs.monthReport;
      }

      return configs;
    },
    stubConfig() {
      return this.reportChartConfigs.stub;
    },
  },
  watch: {
    'currentFilter.properties.gantt_id': async function (projectIds) {
      if (projectIds?.length) {
        this.projectId = projectIds[0];
      } else {
        this.projectId = null;
      }
    },
    'currentFilter.properties.rangeDate': function (range) {
      this.setToolbarRangeValue();
      this.calculateReportByWorker();
    },
    leftSidebarWidth(width) {
      this.clientWidth = `${document.documentElement.clientWidth - width}px`;
    },
    async projectId(projectId) {
      if (projectId) {
        await this.getBaselines();
        this.calculateReportByWorker();
      } else {
        const chartComponent = this.$refs.chart;

        if (chartComponent && chartComponent.instance) {
          chartComponent.instance.dispatchAction({
            type: 'legendAllSelect',
          });
        }

        this.isStatic = true;
        this.updateFilterAdvancedSettings({ isShowNotificationMessage: false });
      }
    },
    calculationMode(newVal) {
      this.$router.replace({
        query: {
          ...this.$route.query,
          calculationModel: newVal,
        },
      });
    },
  },
  created() {
    this.init();
  },
  beforeUnmount() {
    this.appListeners.forEach(event => app.off(event));
  },
  mounted() {
    const reportNameAnalytics = this.pageTitle.replace(/^exmp_chart_/, '').replace(/_title$/, '');

    userExtAnalytics.log('report_open', {
      report_type: reportNameAnalytics,
      calculation_mode: this.calculationMode,
    });

    this.appListeners.push(app.on('onAfterMonthTimeReportCalculation', result => this.setChartData(result)));
    this.appListeners.push(app.on('onAfterMonthBubgetCalculation', result => this.setChartData(result)));

    this.setToolbarRangeValue();
    this.clientWidth = `${document.documentElement.clientWidth - this.leftSidebarWidth}px`;
  },
  methods: {
    init() {
      // this.chartConfig = { ...this.stubConfig };
      this.calculationMode = this.$route?.query?.calculationModel || constants.OVERVIEW_TYPES.ACTUAL_TO_PLANNED;

      if (this.reportType === 'budget') {
        MonthBudgetReport.initCostWorker();
      }

      if (this.reportType === 'time') {
        MonthTimeReport.initTimeWorker();
      }

      if (this.currentFilter?.properties?.gantt_id?.length) {
        this.projectId = this.currentFilter.properties.gantt_id[0];
      }
    },

    calculateReportByWorker() {
      if (this.reportType === 'budget') {
        MonthBudgetReport.calculateCosts(this.allTasks, this.baselineTasks, this.calculationMode);
      }

      if (this.reportType === 'time') {
        MonthTimeReport.calculateTime(this.allTasks, this.baselineTasks, this.calculationMode);
      }
    },

    recalculateChart() {
      if (this.reportType === 'budget') {
        MonthBudgetReport.calculationMode = this.calculationMode;
        MonthBudgetReport.calculateMonthBudgetReport();
      }

      if (this.reportType === 'time') {
        MonthTimeReport.calculationMode = this.calculationMode;
        MonthTimeReport.calculateMonthTimeLogReport();
      }
    },

    setToolbarRangeValue() {
      const range = this.currentFilter?.properties?.rangeDate;

      if (range?.start && range?.end) {
        const start = moment(range.start);
        const end = moment(range.end);

        this.toolbarRange = `${start.date()} ${MONTH_LOCALES[start.month()]} ${start.year()} 
          - ${end.date()} ${MONTH_LOCALES[end.month()]} ${end.year()}`;
      } else {
        this.toolbarRange = '';
      }
    },

    async getBaselines() {
      await this.$store.dispatch('baselines/fetchBaselines', this.projectId);

      const baselines = this.$store.getters['baselines/getProjectBaselines'](this.projectId)
        .map(item => ({ ...item, created_at: new Date(item.created_at) }))
        .sort((a, b) => a.created_at - b.created_at);

      if (baselines.length) {
        this.baselines = baselines;
        this.baselineId = baselines[baselines.length - 1].id;
        await this.getBaselineData(this.baselineId);
        this.updateFilterAdvancedSettings({ isShowNotificationMessage: false });
      } else {
        this.baselines = [];
        const calculationswithBaseline = [constants.OVERVIEW_TYPES.PLANNED_TO_BASELINE, constants.OVERVIEW_TYPES.ACTUAL_TO_BASELINE];

        if (calculationswithBaseline.includes(this.calculationMode)) {
          this.updateFilterAdvancedSettings({ isShowNotificationMessage: true });
        }
        this.calculationMode = constants.OVERVIEW_TYPES.ACTUAL_TO_PLANNED;
        MonthTimeReport.calculationMode = constants.OVERVIEW_TYPES.ACTUAL_TO_PLANNED;
        MonthBudgetReport.calculationMode = constants.OVERVIEW_TYPES.ACTUAL_TO_PLANNED;
      }
    },

    async getBaselineData(id) {
      const baseline = this.baselines.find(b => b.id === id);

      if (!baseline) {
        this.baselineData = {};

        return;
      }

      const data = await this.$store.dispatch('baselines/getBaselineData', { projectId: baseline.gantt_id, eventId: baseline.event_id });

      if (data) {
        this.baselineData = data;
        this.baselineId = id;
      } else {
        this.baselineData = {};
      }
    },

    updateFilterAdvancedSettings(settings) {
      this.$store.commit('filter/updateAdvancedSettingsByFilterType', {
        type: this.currentFilter.type,
        settings,
      });
    },

    setChartData(config) {
      if (config) {
        this.chartConfig = config;
        this.isStatic = false;
      } else {
        this.isStatic = true;
      }
      this.isShowSkeleton = false;
      statusHelper.handlers.hideMainSpinner();
    },

    async onChangeCalculationMode(selected) {
      statusHelper.handlers.showMainSpinner();
      const reportNameAnalytics = this.pageTitle.replace(/^exmp_chart_/, '').replace(/_title$/, '');

      userExtAnalytics.log('report_calculation_mode_change', {
        report_type: reportNameAnalytics,
        from: this.calculationMode,
        to: selected.type,
      });

      this.calculationMode = selected.type;

      if (selected?.baselineId) {
        await this.getBaselineData(selected.baselineId);
        this.calculateReportByWorker();
      } else {
        this.baselineId = null;
        this.recalculateChart();
      }
    },

    toggleSeries(chartInstance) {
      const options = chartInstance.getOption();
      const selected = options.legend[0].selected;
      let series = options.series;

      if (selected && Object.keys(selected).length) {
        series = series.filter(item => {
          if (!(item.name in selected)) {
            return item;
          }

          if (selected[item.name]) {
            return item;
          }
        });

        if (!series.length) {
          return;
        }
      }

      const leftValues = series.filter(item => item.type === 'bar').flatMap(item => item.data.map(obj => obj.value));
      const rightValues = series.filter(item => item.type === 'line').flatMap(item => item.data.map(obj => obj.value));
      const intervals = reportHelpers.calculateYAxisIntervals(leftValues, rightValues);

      this.chartConfig = {
        ...this.chartConfig,
        yAxis: this.chartConfig.yAxis.map((axis, index) => {
          if (index === 0) {
            return { ...axis, interval: intervals.left };
          }
          if (index === 1) {
            return { ...axis, interval: intervals.right };
          }

          return axis;
        }),
      };
    },
  },
};
</script>
